import { useEffect, useMemo, useRef, useState } from 'react'
import { useClickOutsideAndEscape } from 'hooks'
import DropdownButton from './dropdownButton'
import { DropdownInput, InputOptions } from 'types/input'
import { FieldValues, Path, PathValue } from 'react-hook-form'

/**
 *
 * Custom Dropdown element
 * -
 * Need To Pass Following Params
 * - `name` _required_
 * - `options` _required_
 * - `setValue` _required_
 * - `parentClassName` _optional_
 * - `error` _optional_
 * - `className` _optional_
 * - `hintText` _optional_
 * - `label` _Optional_
 * - `defaultValue` _Optional_
 */
const Dropdown = <T extends FieldValues>({
  parentClassName = '',
  className,
  name,
  label,
  defaultValue = undefined,
  setValue,
  hintText,
  options,
  error,
  required = false,
  size = 'medium',
}: DropdownInput<T>) => {
  // To toggle the dropdown visibility
  const [isVisible, setIsVisible] = useState<boolean>(false)

  // To filter out the default value from options if options not provided then first value of option is set
  const defaultSelectedValue = useMemo(() => {
    if (defaultValue === undefined) {
      return options[0]
    }

    return (
      options?.filter((item: InputOptions) => item.value === defaultValue)[0] ||
      options[0]
    )
  }, [defaultValue, options])

  // To handle the selected option, if multiselect is true then count will be displayed instead of object
  const [selected, setSelected] = useState({ ...defaultSelectedValue })

  // To close the dropdown in outside click
  const ref = useRef<HTMLDivElement | null>(null)
  useClickOutsideAndEscape({ ref, callback: () => setIsVisible(false) })

  // to initially set default value in Hook Form
  useEffect(() => {
    setSelected(() => defaultSelectedValue)
    setValue(name, defaultSelectedValue?.value as PathValue<T, Path<T>>)
  }, [defaultValue])

  // Will handle the select field change and add the updated value to register
  const handleValueSelect = (option: InputOptions) => {
    setSelected(option)
    setValue(name, option?.value as PathValue<T, Path<T>>)
    setIsVisible(false)
  }

  //To handle rendering of Button or Checkbox in the dropdown
  const renderInput = useMemo(() => {
    return options?.map((option: InputOptions) => (
      <button
        type="button"
        key={option.value as string}
        value={option.value as string}
        className={`block w-full cursor-pointer rounded-md ${
          size === 'small' ? 'px-1.5 py-1' : 'px-4 py-2.5'
        } text-left outline-none hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-transparent disabled:opacity-30`}
        onClick={() => handleValueSelect(option)}
        disabled={option?.disabled}
      >
        {option.label}
      </button>
    ))
  }, [options])

  return (
    <div className={`relative w-full outline-none ${parentClassName}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="text-xs font-semibold text-sidebar-primary"
        >
          {label}
          {required && <span className="text-error">*</span>}
        </label>
      )}
      {/* Select Button */}
      <div ref={ref}>
        <DropdownButton
          className={className}
          setIsVisible={setIsVisible}
          isVisible={isVisible}
        >
          {selected?.label ?? 'Select'}
        </DropdownButton>

        {/* Options */}
        <div
          className={`absolute z-10 h-max max-h-64 w-full min-w-max origin-top overflow-auto rounded-md bg-white p-2 text-sm shadow-lg transition-all duration-200 ${
            isVisible ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
          }`}
        >
          {renderInput}
        </div>
      </div>

      {/* Error & Hint Text */}
      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 px-3 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default Dropdown
